import { useCallback, useEffect, useRef } from 'react';
import { PAYMENT_CONFIG } from '../../../config/payment';
import { usePaymentFields } from './usePaymentFields';

export function usePaymentSession({ sessionId, onUpdate }) {
  const cleanupRef = useRef(null);
  const { setupFieldValidation } = usePaymentFields();

  const handleSessionUpdate = useCallback((response) => {
    if (response.status === "ok") {
      onUpdate({ type: 'update', response });
    } else if (response.status === "fields_in_error") {
      const errors = [];
      if (response.errors.cardNumber) errors.push("Card number invalid or missing");
      if (response.errors.expiryYear) errors.push("Expiry year invalid or missing");
      if (response.errors.expiryMonth) errors.push("Expiry month invalid or missing");
      if (response.errors.securityCode) errors.push("Security code invalid");
      if (response.errors.nameOnCard) errors.push("Cardholder name invalid or missing");
      
      onUpdate({ 
        type: 'error',
        message: errors.join('. '),
        details: response
      });
    } else {
      onUpdate({ 
        type: 'error',
        message: response.errors?.message || 'Session update failed',
        details: response
      });
    }
  }, [onUpdate]);

  const configureSession = useCallback(() => {
    if (!window.PaymentSession || !sessionId) return;

    try {
      const existingScript = document.getElementById(PAYMENT_CONFIG.SCRIPT_ID);
      if (existingScript) {
        existingScript.remove();
      }

      // Add anti-clickjacking protection
      const style = document.createElement('style');
      style.id = 'antiClickjack';
      style.textContent = 'body{display:none !important;}';
      document.head.appendChild(style);

      const config = {
        session: sessionId,
        fields: {
          card: {
            number: "#card-number",
            securityCode: "#security-code",
            expiryMonth: "#expiry-month",
            expiryYear: "#expiry-year",
            nameOnCard: "#cardholder-name"
          }
        },
        frameEmbeddingMitigation: ["javascript"],
        callbacks: {
          initialized: function(response) {
            if (response.status === "ok") {
              onUpdate({ type: 'initialized', success: true });
              setupFieldValidation(window.PaymentSession);
            } else {
              onUpdate({ 
                type: 'error', 
                message: 'Payment form initialization failed',
                details: response 
              });
            }
          },
          formSessionUpdate: handleSessionUpdate
        },
        interaction: {
          displayControl: {
            formatCard: "EMBOSSED",
            invalidFieldCharacters: "REJECT"
          }
        }
      };

      window.PaymentSession.configure(config);

      // Remove anti-clickjacking protection after configuration
      const antiClickjack = document.getElementById('antiClickjack');
      if (antiClickjack) {
        if (self === top) {
          antiClickjack.remove();
        } else if (top) {
          top.location = self.location;
        }
      }

      cleanupRef.current = () => {
        const fields = ['card-number', 'security-code', 'expiry-month', 'expiry-year', 'cardholder-name'];
        fields.forEach(id => {
          const element = document.getElementById(id);
          if (element) {
            element.innerHTML = '';
            element.style.borderColor = '';
          }
        });

        const antiClickjack = document.getElementById('antiClickjack');
        if (antiClickjack) {
          antiClickjack.remove();
        }
      };
    } catch (error) {
      console.error('Payment session configuration error:', error);
      onUpdate({ 
        type: 'error', 
        message: 'Failed to configure payment session',
        details: error 
      });
    }
  }, [sessionId, onUpdate, setupFieldValidation, handleSessionUpdate]);

  useEffect(() => {
    const timer = setTimeout(configureSession, 100);
    return () => {
      clearTimeout(timer);
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [configureSession]);

  const updateSession = useCallback(() => {
    if (window.PaymentSession) {
      window.PaymentSession.updateSessionFromForm('card');
    }
  }, []);

  return { updateSession };
}